import { CoreModule, provideAbpCore, withOptions } from '@abp/ng.core';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { ThemeSharedModule, provideAbpThemeShared, DEFAULT_VALIDATION_BLUEPRINTS } from '@abp/ng.theme.shared';
import { VALIDATION_BLUEPRINTS } from '@ngx-validate/core';
import { provideIdentityConfig } from '@abp/ng.identity/config';
import { provideAccountConfig } from '@abp/ng.account/config';
import { registerLocale } from '@abp/ng.core/locale';
import { ThemeBasicModule } from '@abp/ng.theme.basic';
import { provideThemeBasicConfig } from '@abp/ng.theme.basic';
import { NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { registerLocaleData } from '@angular/common';
import localeTr from '@angular/common/locales/tr';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { coachProfileInterceptor } from './interceptors/coach-profile.interceptor';
import { validationInterceptor } from './interceptors/validation-interceptor';
import { cultureHeaderInterceptor } from './interceptors/culture-header.interceptor';
import { impersonationInterceptor, ImpersonationBannerComponent } from './impersonation';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_I18N, tr_TR } from 'ng-zorro-antd/i18n';
import * as AllIcons from '@ant-design/icons-angular/icons';
import { IconDefinition } from '@ant-design/icons-angular';
import { NotificationDropdownComponent } from './components/notification-dropdown/notification-dropdown.component';
import { NotificationService } from './services/notification.service';
import { CustomErrorHandler } from './handlers/custom-error-handler';
import { MAGIC_LINK_INITIALIZER_PROVIDER } from './magic-link-initializer';

const antDesignIcons = AllIcons as {
  [key: string]: IconDefinition;
};
const icons: IconDefinition[] = Object.keys(antDesignIcons).map(key => antDesignIcons[key]);

registerLocaleData(localeTr);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    ThemeSharedModule,
    ThemeBasicModule,
    AppRoutingModule,
    NzIconModule.forRoot(icons),
    NotificationDropdownComponent,
    ImpersonationBannerComponent,
  ],
  providers: [
    APP_ROUTE_PROVIDER,
    MAGIC_LINK_INITIALIZER_PROVIDER, // Magic link config initializer - runs BEFORE everything else
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
        skipGetAppConfiguration: false,
      }),
    ),
    provideAbpOAuth(),
    provideIdentityConfig(),
    provideSettingManagementConfig(),
    provideFeatureManagementConfig(),
    provideAccountConfig(),
    provideAbpThemeShared(),
    provideThemeBasicConfig(),
    { provide: LOCALE_ID, useValue: 'tr' },
    { provide: NZ_I18N, useValue: tr_TR },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    {
      provide: VALIDATION_BLUEPRINTS,
      useValue: {
        ...DEFAULT_VALIDATION_BLUEPRINTS,
        required: 'Bu alan zorunludur.',
        minlength: 'Bu alan en az {{ requiredLength }} karakter olmalıdır.',
        maxlength: 'Bu alan en fazla {{ requiredLength }} karakter olabilir.',
        email: 'Geçerli bir e-posta adresi girin.',
        pattern: 'Bu alan geçerli bir formatta değil.',
        min: 'Bu alan en az {{ min }} olmalıdır.',
        max: 'Bu alan en fazla {{ max }} olabilir.',
        invalidDate: 'Geçerli bir tarih girin.',
        invalidPhoneNumber: 'Geçerli bir telefon numarası girin.',
        passwordMismatch: 'Şifreler eşleşmiyor.',
        invalidUrl: 'Geçerli bir URL girin.',
        invalidCreditCard: 'Geçerli bir kredi kartı numarası girin.',
        range: 'Bu alan {{ min }} ile {{ max }} arasında olmalıdır.',
        equalTo: 'Bu alan {{ control }} ile eşleşmelidir.',
        notEqualTo: 'Bu alan {{ control }} ile aynı olmamalıdır.',
        integer: 'Bu alan bir tam sayı olmalıdır.',
        number: 'Bu alan bir sayı olmalıdır.',
        alphanumeric: 'Bu alan sadece harf ve rakam içermelidir.',
        alpha: 'Bu alan sadece harf içermelidir.',
      },
    },
    provideHttpClient(withInterceptors([cultureHeaderInterceptor, coachProfileInterceptor, validationInterceptor, impersonationInterceptor])),
    NotificationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
